import * as React from "react";
import { Box, Typography, BoxProps, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import GlassCard from "./GlassCard";

export interface CardBaseProps extends Omit<BoxProps, 'component' | 'title'> {
    title?: React.ReactNode | string;
    subtitle?: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
    headerActions?: React.ReactNode;
    elevation?: number;
    useGlassCard?: boolean;
    gradientColor?: string;
    glassEffect?: "clear" | "regular" | "none";
    nested?: boolean; // For nested cards that shouldn't blur parent
    glassPerformanceMode?: "auto" | "lightweight" | "full"; // Performance mode for GlassCard
    disableLiquidEffect?: boolean; // Disable liquid glass effect for better performance
}

const StyledCard = styled(Box)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    return {
        // pure background (no opacity, no blur)
        background: isDark ? theme.palette.background.default : theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius * 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: isDark
            ? "0px 2px 8px rgba(0, 0, 0, 0.3), 0px 4px 16px rgba(0, 0, 0, 0.2)"
            : "0px 2px 8px rgba(0, 0, 0, 0.08), 0px 4px 16px rgba(0, 0, 0, 0.04)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            borderRadius: theme.shape.borderRadius * 1.5,
        },
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: isDark
                ? "0px 4px 12px rgba(0, 0, 0, 0.4), 0px 8px 24px rgba(0, 0, 0, 0.3)"
                : "0px 4px 12px rgba(0, 0, 0, 0.12), 0px 8px 24px rgba(0, 0, 0, 0.08)",
        },
    };
});

interface CardSectionProps {
    nested?: boolean;
}

const CardHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.75 : 2.5, nested ? 1.5 : 2),
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    borderTop: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(nested ? 1.5 : 2),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.5 : 2, nested ? 1.5 : 2),
        gap: theme.spacing(nested ? 1.25 : 1.75),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 1 : 1.5, nested ? 0.5 : 0.75),
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(nested ? 0.75 : 1),
        borderTop: "none",
    },
}));

const CardContent = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.5 : 2, nested ? 1.5 : 2),
    position: "relative",
    zIndex: 1,
    borderTop: "none",
    borderBottom: "none",
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.25 : 1.75, nested ? 1.5 : 2),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 0.75 : 1.25, nested ? 0.5 : 0.75),
        borderTop: "none",
        borderBottom: "none",
    },
}));

const CardActions = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.75 : 2.5, nested ? 1.5 : 2),
    borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(nested ? 0.75 : 1),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.25 : 1.75, nested ? 1.75 : 2.5),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 0.75 : 0.75, nested ? 0.5 : 0.75),
        flexDirection: "column",
        alignItems: "stretch",
        gap: theme.spacing(nested ? 0.75 : 1),
    },
}));

const CardBase = React.forwardRef<HTMLDivElement, CardBaseProps>(({
    title,
    subtitle,
    children,
    actions,
    headerActions,
    elevation = 2,
    useGlassCard = false,
    gradientColor,
    glassEffect,
    nested = false,
    glassPerformanceMode = "auto", // Auto-optimize for mobile/desktop
    disableLiquidEffect = false, // Disable liquid effect by default for better performance
    sx,
    ...props
}, ref) => {
    const theme = useTheme();

    // Memoize breakpoint functions to avoid recreation
    const breakpointDown = React.useMemo(
        () => theme.breakpoints.down("sm"),
        [theme.breakpoints]
    );

    // Memoize background gradient
    const backgroundStyle = React.useMemo(() => {
        if (gradientColor) {
            return `linear-gradient(135deg, ${gradientColor}44 0%, rgba(255, 255, 255, 0.3) 100%)`;
        }
        return undefined;
    }, [gradientColor]);

    // Memoize title Typography sx
    const titleSx = React.useMemo(() => ({
        color: "#F4A259",
        fontSize: {
            xs: nested ? "0.95rem" : "1.05rem",
            sm: nested ? "1.05rem" : "1.15rem",
            md: nested ? "1.1rem" : "1.25rem",
        },
        lineHeight: { xs: 1.2, sm: 1.3 },
        [breakpointDown]: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
            maxWidth: "100%",
        },
    }), [nested, breakpointDown]);

    // Memoize subtitle Typography sx
    const subtitleSx = React.useMemo(() => ({
        mt: 0.5,
    }), []);

    // Memoize header Box sx
    const headerBoxSx = React.useMemo(() => ({
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        [breakpointDown]: {
            alignItems: "flex-start",
            px: nested ? 0.5 : 0.75,
        }
    }), [nested, breakpointDown]);

    // Memoize headerActions Box sx
    const headerActionsBoxSx = React.useMemo(() => ({
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        [breakpointDown]: {
            alignItems: "flex-start",
            pt: 0.25,
        }
    }), [breakpointDown]);

    // Memoize CardHeader sx
    const cardHeaderSx = React.useMemo(() => ({
        [breakpointDown]: {
            flexWrap: "nowrap",
            alignItems: "flex-start",
        }
    }), [breakpointDown]);

    // Memoize header content (only re-render when title/subtitle/headerActions change)
    const headerContent = React.useMemo(() => {
        if (!title && !subtitle && !headerActions) return null;

        return (
            <CardHeader
                className="card-base__header"
                nested={nested}
                sx={cardHeaderSx}
            >
                <Box sx={headerBoxSx}>
                    {title && (
                        <Typography
                            variant="h6"
                            component="h2"
                            fontWeight={600}
                            sx={titleSx}
                        >
                            {title}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={subtitleSx}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {headerActions && (
                    <Box sx={headerActionsBoxSx}>
                        {headerActions}
                    </Box>
                )}
            </CardHeader>
        );
    }, [title, subtitle, headerActions, nested, cardHeaderSx, headerBoxSx, titleSx, subtitleSx, headerActionsBoxSx]);

    // Memoize actions content
    const actionsContent = React.useMemo(() => {
        if (!actions) return null;
        return <CardActions className="card-base__actions" nested={nested}>{actions}</CardActions>;
    }, [actions, nested]);

    // Don't memoize full content to allow children to update freely
    const content = (
        <>
            {headerContent}
            <CardContent className="card-base__content" nested={nested}>{children}</CardContent>
            {actionsContent}
        </>
    );

    // Memoize GlassCard sx to avoid recreation
    // Add subtle shadow to make white background visible behind transparent glass card
    const glassCardSx = React.useMemo(() => {
        const isDark = theme.palette.mode === "dark";
        return {
            p: 0,
            // Add subtle shadow to improve visibility of white background
            boxShadow: isDark
                ? "0px 2px 8px rgba(0, 0, 0, 0.2), 0px 4px 16px rgba(0, 0, 0, 0.15)"
                : "0px 2px 8px rgba(0, 0, 0, 0.06), 0px 4px 16px rgba(0, 0, 0, 0.04)",
            ...(backgroundStyle && { background: backgroundStyle }),
            ...sx,
        };
    }, [backgroundStyle, sx, theme.palette.mode]);

    // Memoize GlassCard props to avoid unnecessary re-renders
    const glassCardProps = React.useMemo(() => ({
        effect: glassEffect,
        nested,
        performanceMode: glassPerformanceMode,
        disableLiquidEffect,
        useSimpleGlass: !useGlassCard, // Use simple glass if useGlassCard is false
    }), [glassEffect, nested, glassPerformanceMode, disableLiquidEffect, useGlassCard]);

    // Memoize StyledCard sx to avoid recreation
    const styledCardSx = React.useMemo(() => ({
        ...(backgroundStyle && { background: backgroundStyle }),
        ...sx,
    }), [backgroundStyle, sx]);

    // Filter props to only pass compatible ones to GlassCard
    const {
        color,
        bgcolor,
        ...restProps
    } = props;

    // Always use GlassCard, but with useSimpleGlass prop when useGlassCard is false
    return (
        <GlassCard
            ref={ref}
            {...glassCardProps}
            sx={useGlassCard ? glassCardSx : styledCardSx}
            {...restProps}
        >
            {content}
        </GlassCard>
    );

    return (
        <StyledCard
            ref={ref}
            sx={styledCardSx}
            {...props}
        >
            {content}
        </StyledCard>
    );
});

CardBase.displayName = "CardBase";

export default CardBase;

